import React, { useEffect, useState } from 'react';
import { useAgentContext } from '../contexts/AgentContext';
import { isStandalonePwa } from '../lib/push-notifications';

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: 'accepted' | 'dismissed'; platform: string }>;
};

const DISMISSED_KEY = 'pwa-install-banner-dismissed';

function isIosSafari(): boolean {
  const userAgent = window.navigator.userAgent;
  const isIos = /iPad|iPhone|iPod/.test(userAgent) ||
    (window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1);
  const isSafari = /^((?!CriOS|FxiOS|EdgiOS|OPiOS|mercury).)*Safari/i.test(userAgent);
  return isIos && isSafari;
}

function isMobileBrowser(): boolean {
  const userAgent = window.navigator.userAgent;
  const isTouchMac = window.navigator.platform === 'MacIntel' && window.navigator.maxTouchPoints > 1;
  return /Android|iPad|iPhone|iPod/i.test(userAgent) || isTouchMac;
}

const PwaInstallBanner: React.FC = () => {
  const { isAuthenticated } = useAgentContext();
  const [installPrompt, setInstallPrompt] = useState<BeforeInstallPromptEvent | null>(null);
  const [showIosGuide, setShowIosGuide] = useState(false);
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(DISMISSED_KEY) === 'true');

  useEffect(() => {
    if (!isAuthenticated || dismissed || isStandalonePwa() || !isMobileBrowser()) {
      setInstallPrompt(null);
      setShowIosGuide(false);
      return undefined;
    }

    setShowIosGuide(isIosSafari());

    const handleBeforeInstallPrompt = (event: Event) => {
      event.preventDefault();
      setInstallPrompt(event as BeforeInstallPromptEvent);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    return () => window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
  }, [dismissed, isAuthenticated]);

  const dismiss = () => {
    localStorage.setItem(DISMISSED_KEY, 'true');
    setDismissed(true);
  };

  const install = async () => {
    if (!installPrompt) return;
    await installPrompt.prompt();
    const choice = await installPrompt.userChoice;
    setInstallPrompt(null);
    if (choice.outcome === 'accepted') {
      dismiss();
    }
  };

  if (
    !isAuthenticated ||
    dismissed ||
    isStandalonePwa() ||
    !isMobileBrowser() ||
    (!showIosGuide && !installPrompt)
  ) {
    return null;
  }

  return (
    <div className="bg-primary text-white px-4 py-3 shadow-md">
      <div className="max-w-7xl mx-auto flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
        <div>
          <p className="font-semibold">Instala Asistente Fácil</p>
          {showIosGuide ? (
            <p className="text-sm text-white/90">
              En Safari toca Compartir y luego Agregar a pantalla de inicio.
            </p>
          ) : (
            <p className="text-sm text-white/90">
              Accede más rápido y recibe notificaciones importantes desde tu asistente.
            </p>
          )}
        </div>
        <div className="flex gap-2">
          {installPrompt && (
            <button
              type="button"
              onClick={install}
              className="rounded-lg bg-white px-4 py-2 text-sm font-semibold text-primary hover:bg-white/90"
            >
              Instalar
            </button>
          )}
          <button
            type="button"
            onClick={dismiss}
            className="rounded-lg border border-white/60 px-4 py-2 text-sm font-semibold hover:bg-white/10"
          >
            Ahora no
          </button>
        </div>
      </div>
    </div>
  );
};

export default PwaInstallBanner;
